import { Handle, Position } from '@xyflow/react'

/**
 * HeadFollowNode — face-graph source that rotates the neck chain toward the
 * camera. Strength comes from the parent blend path (sentinel `__headfollow__`
 * weight in the evaluator), not a local alpha — wire through a Blend with
 * `alphaVariable` to fade in/out from the Event Graph.
 */
export function HeadFollowNode({ data, selected }) {
  const maxYaw    = data.maxYaw    ?? 45
  const maxPitch  = data.maxPitch  ?? 25
  const smoothing = data.smoothing ?? 0.15
  const weight    = data._debugWeight ?? 0
  const yaw       = data._debugYaw   ?? 0
  const pitch     = data._debugPitch ?? 0
  const active    = weight > 0.001

  const num = (field, min, max, step) => (
    <input
      type="number"
      min={min} max={max} step={step}
      value={data[field] ?? ({ maxYaw: 45, maxPitch: 25, smoothing: 0.15, deadbandDeg: 2 }[field])}
      onChange={e => data.onChange?.(field, parseFloat(e.target.value))}
      onMouseDown={e => e.stopPropagation()}
      style={{
        width: 56, boxSizing: 'border-box',
        background: '#0a1520', border: '1px solid #1f3850',
        color: '#9ad0f0', padding: '2px 4px',
        borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
      }}
    />
  )

  return (
    <div style={{
      background: selected ? '#0f1a24' : '#08141c',
      border: `2px solid ${active ? '#30c0e0' : selected ? '#2080b0' : '#184858'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 190,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
      boxShadow: active ? '0 0 14px rgba(48,192,224,0.25)' : 'none',
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }}>👀</span>
        <span style={{ color: '#40c0e0', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>HEAD FOLLOW</span>
        {active
          ? <span style={{ color: '#30c0e0', fontSize: 9, marginLeft: 'auto', letterSpacing: 0.3 }}>● {(weight * 100).toFixed(0)}%</span>
          : <span style={{ color: '#2a4858', fontSize: 9, marginLeft: 'auto' }}>○ idle</span>}
      </div>

      {/* Config rows — labels carry a `title` tooltip explaining each knob */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 4, columnGap: 6, marginBottom: 8 }}>
        <span
          title="How far the head can turn left/right toward the camera, in degrees."
          style={{ color: '#90a8b8', fontSize: 10, alignSelf: 'center', cursor: 'help' }}
        >Max Yaw°</span>
        {num('maxYaw', 0, 90, 1)}
        <span
          title="How far the head can tilt up/down toward the camera, in degrees."
          style={{ color: '#90a8b8', fontSize: 10, alignSelf: 'center', cursor: 'help' }}
        >Max Pitch°</span>
        {num('maxPitch', 0, 60, 1)}
        <span
          title="How fast the head reacts to camera movement. Lower = slower / more graceful. Higher = snappier."
          style={{ color: '#90a8b8', fontSize: 10, alignSelf: 'center', cursor: 'help' }}
        >Smoothing</span>
        {num('smoothing', 0.01, 1, 0.01)}
        <span
          title="The camera must move beyond this many degrees from where the head is currently aimed before the head starts following. Filters out tiny camera jitters and gives a small natural delay before the head reacts. Set to 0 to react to every motion; raise for a more 'considered' feel."
          style={{ color: '#90a8b8', fontSize: 10, alignSelf: 'center', cursor: 'help' }}
        >Dead Zone°</span>
        {num('deadbandDeg', 0, 15, 0.5)}
      </div>

      {/* Live readout */}
      <div style={{ color: '#306878', fontSize: 9, marginBottom: 6 }}>
        yaw {yaw.toFixed(0)}° · pitch {pitch.toFixed(0)}°
        <span style={{ marginLeft: 6, color: '#1f4858' }}>
          (lim {maxYaw}°/{maxPitch}°, s={smoothing})
        </span>
      </div>

      {/* Weight bar */}
      <div style={{ height: 3, background: '#0a1a20', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${weight * 100}%`,
          height: '100%',
          background: active ? '#30c0e0' : '#184858',
          transition: 'width 0.1s linear',
        }} />
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="anim"
        style={{ background: active ? '#30c0e0' : '#184858', width: 10, height: 10 }}
      />
    </div>
  )
}
